import axios from "axios";
import { ElMessage } from "element-plus";
import { authService } from "@/services/authService.js";
import store from "@/store";

axios.defaults.baseURL = window.runtimeConfig?.AFP_SERVER_URL;

const getSharedModelToken = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("token");
};

// Axios interceptor to add authentication token to requests
axios.interceptors.request.use(async (config) => {
  const sharedModelToken = getSharedModelToken();

  if (sharedModelToken) {
    return config;
  }

  const token = authService.accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

const RequestOptions = {
  contentType: "application/json",
  accept: "application/json; charset=utf-8",
};

let messageInstance = null;
let syncingInProgress = false;

const Request = {
  createMethod:
    (method) =>
    (url, data, options = {}) => {
      return RequestHandler.handleRequest(url, data, method, options);
    },
  post: (...args) => Request.createMethod("POST")(...args),
  get: (...args) => Request.createMethod("GET")(...args),
  patch: (...args) => Request.createMethod("PATCH")(...args),
  put: (...args) => Request.createMethod("PUT")(...args),
  delete: (...args) => Request.createMethod("DELETE")(...args),
  get_file: (url, data) => {
    return RequestHandler.handleFileRequest(url, data, "POST");
  },
  getCsv: (...args) =>
    Request.createMethod("POST")(...args, { responseType: "blob" }),
};

const RequestHandler = {
  executeRequest: async (requestConfig, retryOnServerError = true) => {
    const makeRequest = () => axios(requestConfig);

    try {
      return await makeRequest();
    } catch (error) {
      if (error.response?.status === 401) {
        try {
          await authService.oidcAuth.startSilentRenew();
          return await makeRequest();
        } catch (refreshError) {
          // Check if we're in a Bobbie iframe
          const isInIframe = window.self !== window.top;
          const isBobbieContext = store.state.auth?.isBobbieContext || false;

          // Show auth guard if in iframe or Bobbie context
          if (isInIframe && isBobbieContext) {
            console.log("Showing auth guard (iframe or Bobbie context)");
            authService.showIframeAuthGuard();
          } else {
            ElMessage.error({
              message: "Session has expired, please log in again to continue.",
              showClose: true,
            });
          }
          throw refreshError;
        }
      } else if (
        retryOnServerError &&
        (!navigator.onLine || error.response?.status >= 500)
      ) {
        return await retryRequest(makeRequest);
      }
      throw error;
    }
  },

  handleRequest: (url, data, method, options) => {
    const {
      contentType = RequestOptions.contentType,
      accept = RequestOptions.accept,
      responseType,
      headers: customHeaders = {},
      ...customConfig
    } = options;

    const requestConfig = {
      method,
      url,
      data,
      headers: {
        Accept: accept,
        "Content-Type": contentType,
        ...customHeaders,
      },
      ...(responseType ? { responseType } : {}),
      ...customConfig,
    };

    return RequestHandler.executeRequest(requestConfig);
  },
  handleFileRequest: (url, data, method) => {
    const requestConfig = {
      method,
      url,
      data,
      responseType: "blob",
    };

    return RequestHandler.executeRequest(requestConfig, false);
  },
};

const retryRequest = async function (promise) {
  try {
    const response = await promise();
    if (syncingInProgress) {
      messageInstance.close();
      messageInstance = ElMessage.success({
        message: "Syncing completed successfully.",
      });
      syncingInProgress = false;
    }
    return response;
  } catch (error) {
    if (!navigator.onLine || (error.response && error.response.status >= 500)) {
      if (!syncingInProgress) {
        messageInstance = ElMessage.warning({
          message: "Syncing in progress. Please don't close this window.",
          duration: 0,
        });
        syncingInProgress = true;
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
      return await retryRequest(promise);
    }
    throw error;
  }
};

export default Request;
